import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { mdHeight, xsHeight } from "components/AppBar";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FacilityPicker } from "components/FacilityPicker";
import {
  EpisodicEventItemFragment,
  FacilityPickerFragment,
  WasteEpisodicEventStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { FederalGeneratorStatusIndicator } from "../Facilities/Facility/FederalGeneratorStatusIndicator";
import { CLOSE_EPISODIC_EVENT_MUTATION, FACILITY_EPISODIC_EVENTS_QUERY } from "./api";
import { unshippedEventWaste } from "./episodicDisplay";
import { EpisodicEventCard } from "./EpisodicEventCard";
import { EpisodicEventDialog } from "./EpisodicEventDialog";

const YEAR_ALLOWANCE_CAPTION = "1 planned + 1 unplanned per year — 40 CFR 262 subpart L";

/**
 * Shown when the page is reached without a `?facilityId=` — this page has no
 * tenant-wide mode since the schema only exposes episodic events per
 * facility. Duplicated from `AccumulationAreasList`'s private
 * `FacilityPickerPrompt` rather than shared, since both copies are a handful
 * of lines that differ only in their prompt text.
 */
function FacilityPickerPrompt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleChange = (facility: FacilityPickerFragment | null) => {
    if (!facility) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("facilityId", facility.id);
    navigate({ search: next.toString() }, { replace: true });
  };

  return (
    <Stack spacing={2} sx={{ px: 3, py: 4, maxWidth: 420 }}>
      <Typography variant="body1">Select a facility to view its episodic events.</Typography>
      <FacilityPicker
        label="Facility"
        facilityQueryVariant="waste"
        value={null}
        onChange={handleChange}
        invalid={false}
        isTouched={false}
        isDirty={false}
        isValidating={false}
      />
    </Stack>
  );
}

/** Opens the declare dialog. */
function DeclareEventButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outlined" size="small" onClick={onClick}>
      Declare new episodic event
    </Button>
  );
}

/**
 * Declare/edit/close dialogs. Each is conditionally rendered so a closed
 * dialog unmounts entirely rather than sitting hidden with stale form state
 * — the create/edit form is a fresh `EpisodicEventDialog` mount every time it
 * opens, matching `WasteContainerDialog`'s inline-create pattern for
 * `AccumulationAreaDialog`. Close has no dedicated dialog file: it's a plain
 * confirmation via the house `ConfirmDialog`.
 */
function EventDialogs({
  facilityId,
  declareOpen,
  onDeclareClose,
  editingEvent,
  onEditClose,
  closingEvent,
  onCloseCancel,
}: {
  facilityId: string;
  declareOpen: boolean;
  onDeclareClose: () => void;
  editingEvent: EpisodicEventItemFragment | null;
  onEditClose: () => void;
  closingEvent: EpisodicEventItemFragment | null;
  onCloseCancel: () => void;
}) {
  const alerts = useAlerts();
  const [closeEvent, { loading: isClosing }] = useMutation(CLOSE_EPISODIC_EVENT_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"],
  });

  const handleConfirmClose = async () => {
    if (!closingEvent) {
      return;
    }
    await closeEvent({
      variables: { id: closingEvent.id },
      onCompleted: () => {
        alerts.success("Episodic event closed");
        onCloseCancel();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while closing the event");
      },
    });
  };

  return (
    <>
      {declareOpen && (
        <EpisodicEventDialog open mode="declare" facilityId={facilityId} onClose={onDeclareClose} />
      )}
      {editingEvent && (
        <EpisodicEventDialog
          open
          mode="edit"
          facilityId={facilityId}
          event={editingEvent}
          onClose={onEditClose}
        />
      )}
      <ConfirmDialog
        open={closingEvent != null}
        onClose={onCloseCancel}
        onConfirm={handleConfirmClose}
        loading={isClosing}
        header="Close this event?"
        info={closeConfirmationInfo(closingEvent)}
        confirmText="Close event"
      />
    </>
  );
}

/**
 * Close-confirmation copy, escalated when event waste is still on site:
 * closing ends the episodic exemption, so unshipped waste becomes routine
 * generation the moment the event closes — worth a louder warning than the
 * plain clock-reversion note.
 */
function closeConfirmationInfo(event: EpisodicEventItemFragment | null): string {
  if (!event) {
    return "";
  }
  const onHand = unshippedEventWaste(event.containers);
  if (onHand.containerCount === 0) {
    return "Attached containers revert to their standing clocks.";
  }
  const containersPhrase =
    onHand.containerCount === 1
      ? "1 container still holds"
      : `${onHand.containerCount} containers still hold`;
  const quantityPhrase = onHand.quantityLabel ? `${onHand.quantityLabel} of ` : "";
  return (
    `${containersPhrase} ${quantityPhrase}waste from this event. ` +
    "Closing makes that waste routine: it counts toward the facility's on-site cap " +
    "and generator status, and the event is recorded as closed with waste unshipped. " +
    "Ship the remaining waste first if it should stay episodic."
  );
}

/** The calendar year of an event's `startDate`. */
function eventYear(event: EpisodicEventItemFragment): number {
  return DateTime.fromISO(event.startDate, { zone: "utc" }).year;
}

/** Distinct event years, newest first — the year picker's options. */
function eventYears(events: EpisodicEventItemFragment[]): number[] {
  return [...new Set(events.map(eventYear))].sort((a, b) => b - a);
}

/**
 * The year picker plus the per-year subpart L allowance note. Only years
 * that actually have events are offered — the picker exists to reach
 * history, not to browse empty years.
 */
function YearPickerRow({
  years,
  year,
  onChange,
}: {
  years: number[];
  year: number;
  onChange: (year: number) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
      <TextField
        select
        size="small"
        label="Year"
        value={year}
        onChange={(e) => {
          // MUI's select passes the MenuItem value through as-is, so
          // e.target.value is the number itself; the string form is only
          // seen if a browser autofill writes raw text. Match on both.
          const parsed = years.find((candidate) => String(candidate) === String(e.target.value));
          if (parsed !== undefined) {
            onChange(parsed);
          }
        }}
        sx={{ minWidth: 120 }}
      >
        {years.map((candidate) => (
          <MenuItem key={candidate} value={candidate}>
            {candidate}
          </MenuItem>
        ))}
      </TextField>
      <Typography variant="caption" color="text.secondary">
        {YEAR_ALLOWANCE_CAPTION}
      </Typography>
    </Stack>
  );
}

function EmptyEventsState({ onDeclare }: { onDeclare: () => void }) {
  return (
    <Box sx={{ py: 4, textAlign: "center", backgroundColor: "grey.50", borderRadius: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        No episodic events have been declared for this facility yet.
      </Typography>
      <DeclareEventButton onClick={onDeclare} />
    </Box>
  );
}

// A facility declares at most 1 planned + 1 unplanned event per year (40 CFR
// 262 subpart L), so 100 is decades of headroom for the page's default,
// unpaginated fetch — no pagination UI exists yet to page past it.
const EVENTS_PAGE_SIZE = 100;

function FacilityEpisodicEvents({ facilityId }: { facilityId: string }) {
  const theme = useTheme();
  const { data, loading } = useQuery(FACILITY_EPISODIC_EVENTS_QUERY, {
    variables: { facilityId, pageSize: EVENTS_PAGE_SIZE },
  });

  const [declareOpen, setDeclareOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EpisodicEventItemFragment | null>(null);
  const [closingEvent, setClosingEvent] = useState<EpisodicEventItemFragment | null>(null);
  // null = "no explicit choice yet"; the newest event year (usually the
  // current one) is the default view.
  const [pickedYear, setPickedYear] = useState<number | null>(null);

  const facilityName = data?.facility?.name ?? "Facility";
  const events = data?.facility?.episodicEvents?.items ?? [];
  const years = eventYears(events);
  const selectedYear = pickedYear ?? years[0] ?? DateTime.now().year;
  const yearEvents = events
    .filter((event) => eventYear(event) === selectedYear)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 2,
        // The events are cards; on a white page they had nothing to sit on.
        backgroundColor: "canvas",
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={2.25}
      >
        <Box>
          <Typography
            component="p"
            variant="overline"
            sx={{ m: 0, mb: 0.5, fontWeight: 700, letterSpacing: ".14em", color: "primary.main" }}
          >
            On-site waste · Episodic events
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
            <Typography
              component="h1"
              variant="h5"
              sx={{ m: 0, fontWeight: 700, lineHeight: 1.1, color: "text.primary" }}
            >
              {facilityName}
            </Typography>
            <FederalGeneratorStatusIndicator
              status={data?.facility?.generationDrivenStatus ?? null}
              active
            />
          </Stack>
          <Typography component="p" variant="body2" sx={{ m: 0, mt: 0.5, color: "text.secondary" }}>
            Temporary generation events that open their own 60-day accumulation window
          </Typography>
        </Box>
        <DeclareEventButton onClick={() => setDeclareOpen(true)} />
      </Stack>

      {loading && events.length === 0 ? (
        <Skeleton variant="rounded" height={220} />
      ) : events.length === 0 ? (
        <EmptyEventsState onDeclare={() => setDeclareOpen(true)} />
      ) : (
        <>
          <YearPickerRow years={years} year={selectedYear} onChange={setPickedYear} />
          {yearEvents.map((event) => {
            const isActive = event.status === WasteEpisodicEventStatus.Active;
            return (
              <EpisodicEventCard
                key={event.id}
                event={event}
                onEdit={isActive ? () => setEditingEvent(event) : undefined}
                onClose={isActive ? () => setClosingEvent(event) : undefined}
              />
            );
          })}
        </>
      )}

      <EventDialogs
        facilityId={facilityId}
        declareOpen={declareOpen}
        onDeclareClose={() => setDeclareOpen(false)}
        editingEvent={editingEvent}
        onEditClose={() => setEditingEvent(null)}
        closingEvent={closingEvent}
        onCloseCancel={() => setClosingEvent(null)}
      />
    </Stack>
  );
}

export function EpisodicEventsPage() {
  const { tenantId } = useTenant();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Episodic events",
    },
  ]);

  if (!tenantId) {
    return null;
  }

  if (!facilityId) {
    return <FacilityPickerPrompt />;
  }

  return <FacilityEpisodicEvents facilityId={facilityId} />;
}
